/* @flow */

import * as React from 'react';

import {getApiTypeFromGraphQLTypename} from 'nutshell-core/utils';

import {TimelineEntryActionMenu} from 'shells/timeline/entry/toolbar/timeline-entry-action-menu';

import type {EventFragmentSparse, SessionUser} from 'nutshell-graphql-types';

import {Summary} from '../../../client_modules/shells/react/components/summary/summary';
import {appHistory} from '../../../history';

import {useRateEntitySummary} from '../../power-ai/timeline-summary/graphql/hooks/use-rate-entity-summary';

import {useCreateComment, useDeleteComment} from '../comments/timeline-comment-mutations';
import {useGetRelatedLeads} from '../graphql/hooks/use-get-related-leads';
import {getEntryTypeFromPayload, getStatusForActivity, getIconForActivity} from '../helpers';

import {TimelineEntry} from './timeline-entry';

type Props = {
    event: EventFragmentSparse,
    currentUser: SessionUser,
    onDeleteEvent?: (eventId: string) => Promise<*>,
    hasGutterLine?: boolean,
};

export function TimelineEntryActivity(props: Props) {
    const {id, changeType, changeTime, actor, payload, reactions, comments} = props.event;
    const createComment = useCreateComment();
    const deleteComment = useDeleteComment();

    const {relatedLeads} = useGetRelatedLeads({changeLogId: id});
    const {rateEntitySummary, isSavingRating} = useRateEntitySummary();

    let hasActivityTranscription = false;

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Activity' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Activity') {
        const entryType = getEntryTypeFromPayload(payload);
        const icon = getIconForActivity(payload);
        const status = getStatusForActivity(payload);

        let content;

        const transcription = payload.transcription;

        // For logged activities, we show the logNote body (which may be undefined)
        if (payload.logNote && payload.logNote.bodyHtml) {
            content = (
                <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: payload.logNote.bodyHtml,
                    }}
                />
            );
        } else if (NutClientConfig.hasClickToCallAi && transcription) {
            // Only show the transcription for an activity if there is no log
            // note
            content = transcription;
        }

        if (NutClientConfig.hasClickToCallAi && transcription) {
            hasActivityTranscription = true;
        }

        const actionMenu = (
            <TimelineEntryActionMenu
                id={id}
                entryType={getEntryTypeFromPayload(payload)}
                handleDelete={props.onDeleteEvent}
            />
        );

        const participants = payload.participants.map((participant) => ({
            type: getApiTypeFromGraphQLTypename(participant.__typename),
            id: participant.id,
            name: participant.name,
            avatarUrl: participant.avatarUrl,
            initials: typeof participant.initials === 'string' ? participant.initials : undefined,
            htmlUrl: typeof participant.htmlUrl === 'string' ? participant.htmlUrl : undefined,
        }));

        if (relatedLeads) {
            relatedLeads.forEach((relatedLead) => {
                participants.push({
                    type: 'leads',
                    id: relatedLead.id,
                    name: relatedLead.name,
                    htmlUrl:
                        typeof relatedLead.htmlUrl === 'string' ? relatedLead.htmlUrl : undefined,
                });
            });
        }

        const handleOpenActivityModal = () => {
            appHistory.replace({
                ...appHistory.location,
                hash: `/${payload.id}`,
                state: {modal: true},
            });
        };

        return (
            <TimelineEntry
                id={id}
                icon={icon}
                entryType={entryType}
                changeType={changeType}
                timestamp={changeTime}
                subject={payload.name}
                content={content}
                status={status || undefined}
                actor={actor}
                reactions={reactions}
                comments={comments}
                onAddComment={(value) =>
                    createComment({
                        changeLogId: id,
                        parentId: payload.id,
                        value,
                        user: props.currentUser,
                    })
                }
                onDeleteComment={(commentId) => deleteComment({changeLogId: id, commentId})}
                currentUser={props.currentUser}
                relatedLeads={
                    relatedLeads
                        ? relatedLeads.map((lead) => ({
                              type: 'leads',
                              id: lead.id,
                              name: lead.name,
                              avatarUrl: lead.avatarUrl,
                              number: lead.number,
                              status: lead.status,
                              priority: lead.priority,
                              htmlUrl: lead.htmlUrl,
                              relatedPerson: lead.contacts.primaryEdge
                                  ? {
                                        type: 'contacts',
                                        id: lead.contacts.primaryEdge.node.id,
                                        name: lead.contacts.primaryEdge.node.name,
                                        htmlUrl: lead.contacts.primaryEdge.node.htmlUrl,
                                    }
                                  : undefined,
                              relatedCompany: lead.accounts.primaryEdge
                                  ? {
                                        type: 'accounts',
                                        id: lead.accounts.primaryEdge.node.id,
                                        name: lead.accounts.primaryEdge.node.name,
                                        htmlUrl: lead.accounts.primaryEdge.node.htmlUrl,
                                    }
                                  : undefined,
                              value: lead.value ? {formatted: lead.value.formatted} : undefined,
                              assignee: lead.assignee
                                  ? {id: lead.assignee.id, name: lead.assignee.name}
                                  : undefined,
                          }))
                        : undefined
                }
                participants={participants}
                onClickOpen={handleOpenActivityModal}
                actionMenu={actionMenu}
                hasGutterLine={props.hasGutterLine}
                externalMeetingTranscriptSummary={
                    payload.externalMeeting &&
                    payload.externalMeeting.transcriptionSummary &&
                    payload.externalMeeting.transcriptionSummary.summary ? (
                        <Summary
                            truncate={{type: 'block', maxLines: 7}}
                            userRating={payload.externalMeeting.transcriptionSummary.userRating}
                            summary={payload.externalMeeting.transcriptionSummary.summary}
                            onRate={(rating) =>
                                rateEntitySummary({
                                    entitySummaryId:
                                        // $FlowIgnore - This is defined at this point
                                        payload.externalMeeting.transcriptionSummary.id,
                                    rating,
                                })
                            }
                            isSavingRating={isSavingRating}
                        />
                    ) : undefined
                }
                hasActivityTranscription={hasActivityTranscription}
            />
        );
    }

    return null;
}
